<script setup lang="ts">
type TriState = 'yes' | 'no' | null;

import type { Business } from '../types/business';
import { parseBooleanParam } from '../helpers/parse-boolean';
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { refreshBusinessCardAnimations } from '../animations/refresh-business-card-animation';
import BusinessCard from './BusinessCard.vue';

const props = defineProps<{
  businesses: Business[];
}>();

function shuffleArray<T>(array: T[]): T[] {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const businessList = ref<Business[]>(shuffleArray(props.businesses.map(b => ({ ...b }))));

const searchText = ref('');
const selectedCategory = ref('');
const openNow = ref<'yes' | null>(null);
const delivery = ref<TriState>(null);
const voucher = ref<TriState>(null);

const categoryOpen = ref(false);
const mobileFiltersOpen = ref(false);

function applyFreshOpenStatus(): boolean {
  const el = document.getElementById('business-data');
  if (!el) return false;
  try {
    const freshData: Business[] = JSON.parse(el.textContent ?? '[]');
    const freshMap = new Map(freshData.map(b => [b.id, b]));
    
    businessList.value = businessList.value.map(b => {
      const fresh = freshMap.get(b.id);
      return fresh ? { ...b, ...fresh } : b;
    });
  } catch {}
  return true;
}

onMounted(() => {
  applyFiltersFromUrl();
  const handler = () => {
    applyFreshOpenStatus();
    document.removeEventListener('afterBlitzInject', handler);
  };
  document.addEventListener('afterBlitzInject', handler);
});

const allCategories = computed(() =>
  [...new Set(businessList.value.flatMap((b) => b.categories))]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b)),
);

const filteredBusinesses = computed(() => {
  const q = searchText.value.trim().toLowerCase();

  return businessList.value.filter((b) => {
    if (q) {
      const hayStack = [b.title, b.url, b.address.formatted, ...b.categories]
        .join(' ')
        .toLowerCase();
      if (!hayStack.includes(q)) {
        return false;
      }
    }

    if (
      selectedCategory.value &&
      !b.categories.includes(selectedCategory.value)
    ) {
      return false;
    }

    if (openNow.value === 'yes' && !b.isOpenNow) {
      return false;
    }

    if (delivery.value !== null) {
      const wanted = delivery.value === 'yes';
      if (b.hasDeliveryService !== wanted) return false;
    }

    if (voucher.value !== null) {
      const wanted = voucher.value === 'yes';
      if (b.acceptsVoucher !== wanted) return false;
    }

    return true;
  });
});

const gridRef = ref<HTMLElement | null>(null);

watch(filteredBusinesses, () => {
  nextTick(() => {
    if (!gridRef.value) return;
    refreshBusinessCardAnimations(gridRef.value);
  });
});

function categoryText(value: string) {
  return value || 'Auswählen...';
}

function toggleCategory() {
  categoryOpen.value = !categoryOpen.value;
}

function chooseCategory(value: string) {
  selectedCategory.value = value;
  categoryOpen.value = false;
}

function onCategoryBlur(e: FocusEvent) {
  const current = e.currentTarget as HTMLElement | null;
  const next = e.relatedTarget as Node | null;
  if (current && next && current.contains(next)) return;
  categoryOpen.value = false;
}

function toggleMobileFilters() {
  mobileFiltersOpen.value = !mobileFiltersOpen.value;
  if (gridRef.value) refreshBusinessCardAnimations(gridRef.value);
  if (!mobileFiltersOpen.value) {
    categoryOpen.value = false;
  }
}

function applyFiltersFromUrl() {
  const params = new URLSearchParams(window.location.search);

  // /geschaefter?voucher=true
  const voucherFromUrl = parseBooleanParam(params.get('voucher'));
  voucher.value =
    voucherFromUrl === null ? null : voucherFromUrl ? 'yes' : 'no';

  // /geschaefter?delivery=true
  const deliveryFromUrl = parseBooleanParam(params.get('delivery'));
  delivery.value =
    deliveryFromUrl === null ? null : deliveryFromUrl ? 'yes' : 'no';

  // /geschaefter?openNow=true
  const openNowFromUrl = parseBooleanParam(params.get('openNow'));
  openNow.value =
    openNowFromUrl === null ? null : openNowFromUrl ? 'yes' : null;

  // /geschaefter?category=Restaurant
  const categoryFromUrl = params.get('category');
  if (categoryFromUrl && allCategories.value.includes(categoryFromUrl)) {
    selectedCategory.value = categoryFromUrl;
  }

  // /geschaefter?search=pizza
  const searchFromUrl = params.get('search');
  if (searchFromUrl) {
    searchText.value = searchFromUrl;
  }
}

type FilterKey = 'category' | 'openNow' | 'delivery' | 'voucher';

const activeFilters = computed(() => {
  const out: Array<{ key: FilterKey; label: string }> = [];
  if (selectedCategory.value) {
    out.push({ key: 'category', label: selectedCategory.value });
  }
  if (openNow.value === 'yes') {
    out.push({ key: 'openNow', label: 'Geöffnet' });
  }
  if (delivery.value) {
    out.push({
      key: 'delivery',
      label: `Lieferservice: ${delivery.value === 'yes' ? 'Ja' : 'Nein'}`,
    });
  }
  if (voucher.value) {
    out.push({
      key: 'voucher',
      label: `Akzeptiert Gutschein: ${voucher.value === 'yes' ? 'Ja' : 'Nein'}`,
    });
  }
  return out;
});

function clearFilter(key: FilterKey) {
  switch (key) {
    case 'category':
      selectedCategory.value = '';
      break;
    case 'openNow':
      openNow.value = null;
      break;
    case 'delivery':
      delivery.value = null;
      break;
    case 'voucher':
      voucher.value = null;
      break;
  }
}
</script>

<template>
  <div class="my-spacebetween">
    <div class="mx-auto min-h-[60vh] w-full max-w-content px-sidespacing">
      <button
        type="button"
        class="filter-mobile-toggle animate-fade"
        :aria-expanded="mobileFiltersOpen ? 'true' : 'false'"
        aria-controls="business-filters"
        @click="toggleMobileFilters"
      >
        <i :class="mobileFiltersOpen ? 'cross-icon' : 'filter-icon'"></i>
      </button>
      <div
        class="filter-grid animate-fade relative z-10"
        id="business-filters"
        :class="{ 'filter-grid--mobile-open': mobileFiltersOpen }"
      >
        <div class="filter-col flex flex-grow flex-col">
          <label class="serach-label" for="text-search">Geschäft</label>
          <div class="search-field__control">
            <input
              v-model="searchText"
              id="text-search"
              type="text"
              placeholder="Deine Suche..."
              class="search-field__input"
            />
            <span class="search-icon search-field__icon"></span>
          </div>
        </div>

        <div
          class="select-field filter-col filter-col--advanced flex flex-grow flex-col"
          :class="{ 'select-field--open': categoryOpen }"
          @focusout="onCategoryBlur"
        >
          <label class="select-field__label" id="category-label"
            >Kategorie</label
          >

          <div class="select-field__control m:mt-2">
            <button
              id="category-trigger"
              type="button"
              class="select-field__trigger"
              aria-haspopup="listbox"
              :aria-expanded="categoryOpen ? 'true' : 'false'"
              aria-labelledby="category-label category-trigger"
              @click="toggleCategory"
            >
              <span>{{ categoryText(selectedCategory) }}</span>
              <span
                class="apple-arrow select-field__arrow"
                aria-hidden="true"
              ></span>
            </button>

            <ul
              v-if="categoryOpen"
              id="category-listbox"
              class="select-field__list"
              role="listbox"
              aria-labelledby="category-label"
            >
              <li>
                <button
                  type="button"
                  class="select-field__option"
                  :class="{
                    'select-field__option--active': selectedCategory === '',
                  }"
                  @click="chooseCategory('')"
                >
                  Auswaehlen...
                </button>
              </li>
              <li v-for="category in allCategories" :key="category">
                <button
                  type="button"
                  class="select-field__option"
                  :class="{
                    'select-field__option--active':
                      selectedCategory === category,
                  }"
                  @click="chooseCategory(category)"
                >
                  {{ category }}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div class="filter-col filter-col--advanced flex flex-col">
          <label
            ><i class="door-open mr-2 -translate-y-[0.125rem]"></i>Jetzt
            geöffnet</label
          >
          <div class="filter-radio-button">
            <input
              id="open-yes"
              v-model="openNow"
              type="radio"
              name="open"
              value="yes"
            />
            <label for="open-yes">Ja</label>
          </div>
        </div>

        <div class="filter-col filter-col--advanced flex flex-col">
          <label>Lieferservice</label>
          <div class="filter-radio-button-wrapper">
            <div class="filter-radio-button">
              <input
                id="delivery-yes"
                v-model="delivery"
                type="radio"
                name="delivery"
                value="yes"
              />
              <label for="delivery-yes">Ja</label>
            </div>
            <div class="filter-radio-button">
              <input
                id="delivery-no"
                v-model="delivery"
                type="radio"
                name="delivery"
                value="no"
              />
              <label for="delivery-no">Nein</label>
            </div>
          </div>
        </div>

        <div class="filter-col filter-col--advanced flex flex-col">
          <label
            ><i
              class="voucher-icon mr-2 -translate-y-[0.25rem] align-middle text-primary-900"
            ></i
            >Akzeptiert Gutschein</label
          >
          <div class="filter-radio-button-wrapper">
            <div class="filter-radio-button">
              <input
                id="voucher-yes"
                v-model="voucher"
                type="radio"
                name="voucher"
                value="yes"
              />
              <label for="voucher-yes">Ja</label>
            </div>
            <div class="filter-radio-button">
              <input
                id="voucher-no"
                v-model="voucher"
                type="radio"
                name="voucher"
                value="no"
              />
              <label for="voucher-no">Nein</label>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-8 flex flex-row-reverse flex-nowrap justify-between">
        <div class="flex-shrink-0">
          {{ filteredBusinesses.length }} / {{ businessList.length }}
        </div>
        <div
          v-if="activeFilters.length"
          class="flex items-center justify-center gap-2"
        >
          <div class="flex flex-wrap gap-4">
            <button
              v-for="f in activeFilters"
              :key="f.key"
              type="button"
              @click="clearFilter(f.key)"
              class="w-fit rounded-md bg-primary-900 px-3 py-2 font-bold uppercase text-white"
            >
              {{ f.label }} <span class="ml-2 text-base">x</span>
            </button>
          </div>
        </div>
      </div>

      <div
        ref="gridRef"
        class="mt-10 grid grid-cols-2 gap-x-20 gap-y-20 m:grid-cols-1"
      >
        <BusinessCard
          v-for="business in filteredBusinesses"
          :key="business.id"
          :business="business"
          class="animate-fade"
        />
      </div>
    </div>
  </div>
</template>
